'use client'

import { GlobalSidebar } from '@/components/ui/global-sidebar'
import { Button } from '@/components/ui/button'
import { Layout, Grid, Grid2x2, Grid3x3, GripVertical, Eye, EyeOff } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

type GridLayout = '2x2' | '3x3'

interface Widget {
  id: string
  title: string
  component: string
  visible: boolean
  order: number
  fixed?: boolean
}

interface LayoutArrangerSidebarProps {
  isOpen: boolean
  onClose: () => void
  widgets: Widget[]
  gridLayout: GridLayout
  onWidgetsChange: (widgets: Widget[]) => void
  onGridLayoutChange: (layout: GridLayout) => void
  title?: string
  subtitle?: string
}

function SortableWidgetItem({
  widget,
  onToggleVisibility
}: {
  widget: Widget
  onToggleVisibility: (id: string) => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: widget.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-3 p-3 rounded-lg border bg-card',
        isDragging && 'opacity-50 shadow-lg z-50'
      )}
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing flex-shrink-0 text-muted-foreground hover:text-foreground"
      >
        <GripVertical className="h-5 w-5" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm truncate">{widget.title}</div>
        <div className="text-xs text-muted-foreground">
          {widget.visible ? 'Visible' : 'Hidden'}
        </div>
      </div>

      <Button
        variant="ghost"
        size="icon"
        onClick={() => onToggleVisibility(widget.id)}
        className="flex-shrink-0"
      >
        {widget.visible ? (
          <Eye className="h-4 w-4 text-primary" />
        ) : (
          <EyeOff className="h-4 w-4 text-muted-foreground" />
        )}
      </Button>
    </div>
  )
}

export function LayoutArrangerSidebar({
  isOpen,
  onClose,
  widgets,
  gridLayout,
  onWidgetsChange,
  onGridLayoutChange,
  title = 'Arrange Layout',
  subtitle
}: LayoutArrangerSidebarProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = widgets.findIndex(w => w.id === active.id)
      const newIndex = widgets.findIndex(w => w.id === over.id)

      const reordered = arrayMove(widgets, oldIndex, newIndex).map((w, index) => ({
        ...w,
        order: index
      }))

      onWidgetsChange(reordered)
    }
  }

  const handleToggleVisibility = (id: string) => {
    const updated = widgets.map(w =>
      w.id === id ? { ...w, visible: !w.visible } : w
    )
    onWidgetsChange(updated)
  }

  const visibleCount = widgets.filter(w => w.visible).length
  const finalSubtitle = subtitle || `${visibleCount} of ${widgets.length} widgets visible`

  return (
    <GlobalSidebar
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle={finalSubtitle}
      icon={<Layout className="h-5 w-5" />}
      width="md"
    >
      <div className="space-y-6">
        {/* Grid Layout Selector */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Grid className="h-4 w-4" />
              Layout Grid
            </h3>
            <div className="inline-flex rounded-lg border border-border p-1 bg-muted/50">
              <Button
                variant={gridLayout === '2x2' ? 'default' : 'ghost'}
                size="sm"
                className={cn(
                  'rounded-md transition-all px-3',
                  gridLayout === '2x2' && 'shadow-sm'
                )}
                onClick={() => onGridLayoutChange('2x2')}
              >
                <Grid2x2 className="h-4 w-4 mr-2" />
                2×2
              </Button>
              <Button
                variant={gridLayout === '3x3' ? 'default' : 'ghost'}
                size="sm"
                className={cn(
                  'rounded-md transition-all px-3',
                  gridLayout === '3x3' && 'shadow-sm'
                )}
                onClick={() => onGridLayoutChange('3x3')}
              >
                <Grid3x3 className="h-4 w-4 mr-2" />
                3×3
              </Button>
            </div>
          </div>
        </div>

        {/* Widget List */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Widgets
            </h3>
          </div>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={widgets.map(w => w.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {widgets.map(widget => (
                  <SortableWidgetItem
                    key={widget.id}
                    widget={widget}
                    onToggleVisibility={handleToggleVisibility}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      </div>
    </GlobalSidebar>
  )
}
